import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Stack,
  LinearProgress,
  Chip,
  Fade,
  Grow,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
  Snackbar,
  Slide,
  Grid,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { processLatexString } from '../../../components/LatexRenderer';
import demoService from '../../../services/demoApi';
import DemoProgressCTA from './DemoProgressCTA';
import DemoCompletionCTA from './DemoCompletionCTA';
import analytics from '../../../services/analytics';

const DemoQuizInterface = ({ examData, onCTAClick, onBackToLanding, demoAnalytics }) => {
  const theme = useTheme();
  
  // State management
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({}); // {questionId: 'A'/'B'/'C'/'D'}
  const [showResults, setShowResults] = useState({}); // {questionId: boolean}
  const [correctAnswers, setCorrectAnswers] = useState({}); // {questionId: 'A'/'B'/'C'/'D'}
  const [loadingAnswer, setLoadingAnswer] = useState(false);
  const [showFinalResults, setShowFinalResults] = useState(false);
  const [showExtendedAnswers, setShowExtendedAnswers] = useState({}); // {questionId: boolean}
  const [extendedAnswers, setExtendedAnswers] = useState({}); // {questionId: answerText}
  const [imageMap, setImageMap] = useState({});
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  const [comingSoonMessage, setComingSoonMessage] = useState('');
  
  useEffect(() => {
    // Fetch demo images when component mounts
    const fetchDemoImages = async () => {
      try {
        const images = await demoService.getImages();
        const map = {};
        images.forEach(img => {
          map[img.filename] = img.id;
          const baseFilename = img.filename.replace(/\.[^/.]+$/, '');
          map[baseFilename] = img.id;
        });
        setImageMap(map);
        setImagesLoaded(true);
      } catch (error) {
        console.error('Error fetching demo images:', error);
        setImagesLoaded(true); // Set to true even on error to avoid blocking UI
      }
    };

    fetchDemoImages();
  }, []);
  
  if (!examData || !examData.questions) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary">
          No exam data available
        </Typography>
        <Button onClick={onBackToLanding} sx={{ mt: 2 }}>
          Return to Generator
        </Button>
      </Box>
    );
  }

  const { questions } = examData;
  const { mcq_questions = [], extended_questions = [] } = questions;
  const allQuestions = [...mcq_questions, ...extended_questions];
  const currentQuestion = allQuestions[currentQuestionIndex];
  const isMCQ = currentQuestionIndex < mcq_questions.length;
  
  // Calculate progress
  const progress = ((currentQuestionIndex + 1) / allQuestions.length) * 100;
  
  // Calculate score
  const calculateScore = () => {
    let correct = 0;
    let total = 0;
    
    mcq_questions.forEach(q => {
      if (showResults[q.id]) {
        total++;
        if (selectedAnswers[q.id] === correctAnswers[q.id]) {
          correct++;
        }
      }
    });
    
    return { correct, total };
  };

  const parseQuestionContent = (question) => {
    // Use templated content if available
    if (question.templated_content) {
      const questionText = question.templated_content.question || '';
      const options = ['a', 'b', 'c', 'd'].map(opt => ({
        label: opt.toUpperCase(),
        text: question.templated_content[`option_${opt}`] || ''
      })).filter(opt => opt.text);
      
      return { questionText, options };
    }
    
    // Fall back to parsing raw content
    const content = question.content || '';
    const parts = content.split('\\choice');
    const questionText = parts[0].replace(/\\question\[\d+\]/, '').trim();
    
    // Extract options
    const options = parts.slice(1).map((opt, i) => {
      let optText = opt.trim();
      optText = optText.replace(/\\end\{oneparchoices\}.*$/s, '').trim();
      return {
        label: String.fromCharCode(65 + i), // A, B, C, D
        text: optText
      };
    }).filter(opt => opt.text);

    return { questionText, options };
  };

  const handleOptionSelect = (option) => {
    if (!showResults[currentQuestion.id]) {
      setSelectedAnswers({
        ...selectedAnswers,
        [currentQuestion.id]: option
      });
    }
  };

  const handleCheckAnswer = async () => {
    if (!currentQuestion.demo_question_id) return;
    
    setLoadingAnswer(true);
    try {
      const answerData = await demoService.getQuestionAnswer(currentQuestion.demo_question_id);
      
      if (answerData && answerData.answer !== undefined && answerData.answer !== null) {
        const correctAnswer = answerData.answer;
        const userAnswer = selectedAnswers[currentQuestion.id];
        const isCorrect = isMCQ ? userAnswer === correctAnswer : true; // Extended questions don't have right/wrong
        
        setCorrectAnswers({
          ...correctAnswers,
          [currentQuestion.id]: correctAnswer
        });
        
        setShowResults({
          ...showResults,
          [currentQuestion.id]: true
        });
        
        // Track question answered in demo analytics
        if (demoAnalytics) {
          demoAnalytics.trackQuestionAnswered({
            type: isMCQ ? 'mcq' : 'extended',
            questionNumber: currentQuestionIndex + 1,
            questionId: currentQuestion.id,
            isCorrect: isCorrect,
            userAnswer: userAnswer,
            correctAnswer: correctAnswer
          });
        }
        
        // For extended questions, store the answer text
        if (!isMCQ) {
          setExtendedAnswers({
            ...extendedAnswers,
            [currentQuestion.id]: answerData.answer || 'prove_placeholder'
          });
          setShowExtendedAnswers({
            ...showExtendedAnswers,
            [currentQuestion.id]: true
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch answer:', error);
    } finally {
      setLoadingAnswer(false);
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < allQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      setShowFinalResults(true);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleHintClick = () => {
    // Track hint button interaction in PostHog
    analytics.track('demo_hint_clicked', {
      question_id: currentQuestion.id,
      question_number: currentQuestionIndex + 1,
      question_type: isMCQ ? 'mcq' : 'extended',
      has_answered: !!selectedAnswers[currentQuestion.id],
      timestamp: new Date().toISOString()
    });

    // Track hint click in demo analytics
    if (demoAnalytics) {
      demoAnalytics.trackHintClick(currentQuestionIndex + 1);
    }

    setComingSoonMessage('💡 Smart hints are coming soon! Get early access to unlock AI-powered study assistance.');
    setComingSoonOpen(true);
  };

  const handleAIChatClick = () => {
    // Track AI chat button interaction in PostHog
    analytics.track('demo_ai_chat_clicked', {
      question_id: currentQuestion.id,
      question_number: currentQuestionIndex + 1,
      question_type: isMCQ ? 'mcq' : 'extended',
      has_answered: !!selectedAnswers[currentQuestion.id],
      timestamp: new Date().toISOString()
    });

    // Track AI chat click in demo analytics
    if (demoAnalytics) {
      demoAnalytics.trackAIChatClick(currentQuestionIndex + 1);
    }

    setComingSoonMessage('🤖 AI tutoring is coming soon! Sign up now to get personalized help with every question.');
    setComingSoonOpen(true);
  };

  const handleComingSoonClose = () => {
    setComingSoonOpen(false);
  };

  const renderMCQQuestion = () => {
    const { questionText, options } = parseQuestionContent(currentQuestion);
    const selectedOption = selectedAnswers[currentQuestion.id];
    const correctOption = correctAnswers[currentQuestion.id];
    const hasResult = showResults[currentQuestion.id];
    
    return (
      <Box>
        <Box sx={{ mb: 4 }}>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
            {processLatexString(questionText, 'demo', imageMap, 'mcq-question')}
          </Typography>
        </Box>
        
        <Grid container spacing={2}>
          {options.map((option) => {
            const isSelected = selectedOption === option.label;
            const isCorrect = correctOption === option.label;
            const showCorrect = hasResult && isCorrect;
            const showIncorrect = hasResult && isSelected && !isCorrect;
            
            return (
              <Grid item xs={12} sm={6} key={option.label}>
                <Paper
                  onClick={() => handleOptionSelect(option.label)}
                  sx={{
                    p: 2.5,
                    height: '100%',
                    cursor: hasResult ? 'default' : 'pointer',
                    border: `2px solid ${
                      showCorrect ? theme.palette.success.main :
                      showIncorrect ? theme.palette.error.main :
                      isSelected ? theme.palette.primary.main :
                      theme.palette.divider
                    }`,
                    backgroundColor: 
                      showCorrect ? alpha(theme.palette.success.main, 0.1) :
                      showIncorrect ? alpha(theme.palette.error.main, 0.1) :
                      isSelected ? alpha(theme.palette.primary.main, 0.05) :
                      'transparent',
                    transition: 'all 0.3s ease',
                    '&:hover': !hasResult ? {
                      borderColor: theme.palette.primary.main,
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      transform: 'translateY(-2px)',
                      boxShadow: theme.shadows[3]
                    } : {},
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Typography variant="body2" sx={{ fontWeight: isSelected ? 600 : 400, flex: 1 }}>
                      <Box component="span" sx={{ fontWeight: 700, mr: 1 }}>
                        {option.label}.
                      </Box>
                      {processLatexString(option.text, 'demo', imageMap, 'mcq-option')}
                    </Typography>
                    {showCorrect && (
                      <CheckCircleIcon color="success" sx={{ fontSize: 24, flexShrink: 0 }} />
                    )}
                    {showIncorrect && (
                      <CancelIcon color="error" sx={{ fontSize: 24, flexShrink: 0 }} />
                    )}
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
        
        {!hasResult && (
          <Box sx={{ mt: 4 }}>
            <Button
              variant="contained"
              onClick={handleCheckAnswer}
              disabled={!selectedOption || loadingAnswer}
              sx={{ 
                px: 3, 
                py: 1,
                fontWeight: 600,
                mb: 2
              }}
              fullWidth
            >
              {loadingAnswer ? <CircularProgress size={24} color="inherit" /> : 'Check Answer'}
            </Button>
            
            {/* Hint and AI Chat buttons */}
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<LightbulbIcon />}
                onClick={handleHintClick}
                sx={{
                  flex: 1,
                  py: 0.5,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  borderColor: alpha(theme.palette.warning.main, 0.5),
                  color: theme.palette.warning.main,
                  '&:hover': {
                    borderColor: theme.palette.warning.main,
                    backgroundColor: alpha(theme.palette.warning.main, 0.08),
                  }
                }}
              >
                Get a Hint
              </Button>
              <Button
                variant="outlined"
                startIcon={<SmartToyIcon />}
                onClick={handleAIChatClick}
                sx={{
                  flex: 1,
                  py: 0.5,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  borderColor: alpha(theme.palette.info.main, 0.5),
                  color: theme.palette.info.main,
                  '&:hover': {
                    borderColor: theme.palette.info.main,
                    backgroundColor: alpha(theme.palette.info.main, 0.08),
                  }
                }}
              >
                Ask AI Tutor
              </Button>
            </Stack>
          </Box>
        )}
        
        {hasResult && (
          <Fade in={true}>
            <Alert 
              severity={selectedOption === correctOption ? 'success' : 'error'}
              sx={{ 
                mt: 4,
                '& .MuiAlert-message': { fontWeight: 500 }
              }}
            >
              {selectedOption === correctOption ? 
                'Correct! Well done!' : 
                `Incorrect. The correct answer is ${correctOption}.`
              }
            </Alert>
          </Fade>
        )}
        
        {/* Progress CTA after every 3 questions */}
        {hasResult && (currentQuestionIndex + 1) % 3 === 0 && (
          <DemoProgressCTA 
            onCTAClick={() => onCTAClick('progress', `quiz_question_${currentQuestionIndex + 1}`)}
            questionNumber={currentQuestionIndex + 1}
            totalQuestions={allQuestions.length}
          />
        )}
      </Box>
    );
  };

  const renderExtendedQuestion = () => {
    let content = '';
    
    // Check if this is a multi-part question
    const hasParts = currentQuestion.templated_content?.has_parts && currentQuestion.templated_content?.parts?.length > 0;
    
    // Use templated content if available
    if (currentQuestion.templated_content && currentQuestion.templated_content.question) {
      content = currentQuestion.templated_content.question;
    } else if (!hasParts) {
      // Only fall back to parsing raw content if this is NOT a multi-part question
      // For multi-part questions, the question content is in the parts themselves
      content = currentQuestion.content.replace(/\\question\[\d+\]/, '').trim();
    }
    
    const showAnswer = showExtendedAnswers[currentQuestion.id];
    const answer = extendedAnswers[currentQuestion.id];
    
    return (
      <Box>
        {/* Only render main question content if it exists */}
        {content && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
              {processLatexString(content, 'demo', imageMap)}
            </Typography>
          </Box>
        )}
        
        {/* Render parts if this is a multi-part question */}
        {hasParts && (
          <Box sx={{ mb: 4, pl: 2 }}>
            {currentQuestion.templated_content.parts.map((part, partIndex) => (
              <Box key={partIndex} sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  <Box component="span" sx={{ fontWeight: 700, mr: 1 }}>
                    ({String.fromCharCode(96 + part.part_number)})
                  </Box>
                  {processLatexString(part.content, 'demo', imageMap)}
                  {part.marks && (
                    <Chip 
                      size="small" 
                      label={`${part.marks} mark${part.marks > 1 ? 's' : ''}`} 
                      sx={{ ml: 2, verticalAlign: 'middle' }} 
                    />
                  )}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
        
        {!showAnswer ? (
          <Box sx={{ mt: 4 }}>
            <Button
              variant="contained"
              startIcon={<VisibilityIcon />}
              onClick={handleCheckAnswer}
              disabled={loadingAnswer}
              sx={{ 
                px: 3, 
                py: 1,
                fontWeight: 600,
                mb: 2
              }}
              fullWidth
            >
              {loadingAnswer ? <CircularProgress size={24} color="inherit" /> : 'Show Answer'}
            </Button>
            
            {/* Hint and AI Chat buttons for extended questions */}
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<LightbulbIcon />}
                onClick={handleHintClick}
                sx={{
                  flex: 1,
                  py: 0.5,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  borderColor: alpha(theme.palette.warning.main, 0.5),
                  color: theme.palette.warning.main,
                  '&:hover': {
                    borderColor: theme.palette.warning.main,
                    backgroundColor: alpha(theme.palette.warning.main, 0.08),
                  }
                }}
              >
                Get a Hint
              </Button>
              <Button
                variant="outlined"
                startIcon={<SmartToyIcon />}
                onClick={handleAIChatClick}
                sx={{
                  flex: 1,
                  py: 0.5,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  borderColor: alpha(theme.palette.info.main, 0.5),
                  color: theme.palette.info.main,
                  '&:hover': {
                    borderColor: theme.palette.info.main,
                    backgroundColor: alpha(theme.palette.info.main, 0.08),
                  }
                }}
              >
                Ask AI Tutor
              </Button>
            </Stack>
          </Box>
        ) : (
          <Fade in={true}>
            <Paper 
              sx={{ 
                p: 4, 
                mt: 4,
                backgroundColor: alpha(theme.palette.success.main, 0.05),
                border: `1px solid ${theme.palette.success.main}`,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'success.main' }}>
                Answer:
              </Typography>
              {hasParts && currentQuestion.templated_content?.parts ? (
                <Box>
                  {currentQuestion.templated_content.parts.map((part, partIndex) => (
                    <Box key={partIndex} sx={{ mb: 3 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        Part ({String.fromCharCode(96 + part.part_number)}):
                      </Typography>
                      {!part.answer || part.answer.trim() === '' ? (
                        <Box sx={{ 
                          pl: 2,
                          py: 1,
                          ml: 2,
                          backgroundColor: alpha(theme.palette.info.main, 0.1),
                          borderRadius: 1,
                          border: `1px dashed ${theme.palette.info.main}`
                        }}>
                          <Typography variant="body2" sx={{ lineHeight: 1.6, fontStyle: 'italic', color: theme.palette.info.main }}>
                            Full working for prove questions will be provided in the app
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ pl: 2, lineHeight: 1.6 }}>
                          {processLatexString(part.answer, 'demo', imageMap)}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              ) : (
                answer === 'prove_placeholder' || !answer || answer.trim() === '' ? (
                  <Box sx={{ 
                    p: 2, 
                    backgroundColor: alpha(theme.palette.info.main, 0.1),
                    borderRadius: 1,
                    border: `1px dashed ${theme.palette.info.main}`
                  }}>
                    <Typography variant="body2" sx={{ lineHeight: 1.6, fontStyle: 'italic', color: theme.palette.info.main }}>
                      Full working for prove questions will be provided in the app
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                    {processLatexString(answer, 'demo', imageMap)}
                  </Typography>
                )
              )}
            </Paper>
          </Fade>
        )}
      </Box>
    );
  };

  const renderFinalResults = () => {
    const { correct, total } = calculateScore();
    const percentage = total > 0 ? Math.round((correct / total) * 100) : 0;
    
    // Track quiz completion in demo analytics
    if (demoAnalytics) {
      demoAnalytics.trackQuizCompletion(percentage / 100);
    }
    
    return (
      <Grow in={true}>
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
            Quiz Complete!
          </Typography>
          
          <Paper sx={{ 
            p: 6, 
            my: 4, 
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
          }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Your Score: {correct}/{total}
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
              {percentage}% Correct
            </Typography>
            
            {percentage >= 80 && (
              <Typography variant="body1" sx={{ color: 'success.main', fontWeight: 500 }}>
                Excellent work! You're well prepared!
              </Typography>
            )}
            {percentage >= 60 && percentage < 80 && (
              <Typography variant="body1" sx={{ color: 'warning.main', fontWeight: 500 }}>
                Good effort! Keep practicing to improve.
              </Typography>
            )}
            {percentage < 60 && (
              <Typography variant="body1" sx={{ color: 'error.main', fontWeight: 500 }}>
                Keep studying! Practice makes perfect.
              </Typography>
            )}
          </Paper>
          
          <DemoCompletionCTA 
            onCTAClick={() => onCTAClick('completion', 'quiz_completion')}
            examData={examData}
            mode="quiz"
            score={percentage}
            questionsCompleted={total}
          />
        </Box>
      </Grow>
    );
  };

  if (showFinalResults) {
    return renderFinalResults();
  }

  // Show loading state while images are being fetched
  if (!imagesLoaded) {
    return (
      <Box sx={{ maxWidth: 900, mx: 'auto', textAlign: 'center', py: 8 }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
          Loading exam resources...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Question {currentQuestionIndex + 1} of {allQuestions.length}
          </Typography>
          <Chip 
            label={isMCQ ? '1 mark' : `${currentQuestion.marks} marks`}
            color="primary"
            sx={{ fontWeight: 500 }}
          />
        </Box>
        
        <LinearProgress 
          variant="determinate" 
          value={progress} 
          sx={{ 
            height: 10, 
            borderRadius: 5,
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            '& .MuiLinearProgress-bar': {
              borderRadius: 5
            }
          }}
        />
      </Box>
      
      {/* Question Content */}
      <Paper sx={{ p: 3, minHeight: 400, mb: 4 }}>
        {isMCQ ? renderMCQQuestion() : renderExtendedQuestion()}
      </Paper>
      
      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handlePrevious}
          disabled={currentQuestionIndex === 0}
          sx={{ px: 2.5 }}
        >
          Previous
        </Button>
        
        <Button
          endIcon={<ArrowForwardIcon />}
          onClick={handleNext}
          variant="contained"
          sx={{ px: 2.5 }}
        >
          {currentQuestionIndex === allQuestions.length - 1 ? 'Finish' : 'Next'}
        </Button>
      </Box>
      
      {/* Progress Summary */}
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {Object.keys(showResults).length} of {mcq_questions.length} MCQ questions answered
        </Typography>
      </Box>
      
      {/* Coming Soon Notification */}
      <Snackbar
        open={comingSoonOpen}
        autoHideDuration={4000}
        onClose={handleComingSoonClose}
        anchorOrigin={{ vertical: 'center', horizontal: 'center' }}
        TransitionComponent={Slide}
        TransitionProps={{ direction: 'up' }}
      >
        <Alert 
          onClose={handleComingSoonClose} 
          severity="info" 
          variant="filled"
          icon={<AutoAwesomeIcon />}
          sx={{ 
            minWidth: 300,
            maxWidth: 500,
            fontWeight: 500,
            borderRadius: 2,
            boxShadow: theme.shadows[8],
            '& .MuiAlert-message': {
              padding: '8px 0',
            }
          }}
        >
          {comingSoonMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DemoQuizInterface;